import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import companyService from '../../../../services/companyService';
import type { ICompany } from '../../../../types/company';
import Loader from '../../../../components/common/Loader';
import Modal from '../../../../components/common/Modal';
import FormField from '../../../../components/common/FormField';
import { showToast } from '../../../../utils/showToast';
import { Plus, Edit, Trash2 } from 'lucide-react';

const companySchema = Yup.object().shape({
  name: Yup.string().required('Company name is required'),
  logo: Yup.string().url('Must be a valid URL').required('Logo URL is required'),
  featured: Yup.boolean(),
});

const ManageCompanies: React.FC = () => {
  const [companies, setCompanies] = useState<ICompany[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<ICompany | null>(null);

  const fetchCompanies = async () => {
    setIsLoading(true);
    try {
      const data = await companyService.getAllCompanies();
      setCompanies(data);
    } catch (err: any) {
      showToast(err.message, { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const formik = useFormik({
    initialValues: {
      name: editingCompany?.name || '',
      logo: editingCompany?.logo || '',
      featured: editingCompany?.featured || false,
    },
    validationSchema: companySchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        setSubmitting(true);
        if (editingCompany) {
          await companyService.updateCompany(editingCompany._id, values);
          showToast('Company updated successfully!', { type: 'success' });
        } else {
          await companyService.createCompany(values);
          showToast('Company added successfully!', { type: 'success' });
        }
        resetForm();
        setIsModalOpen(false);
        setEditingCompany(null);
        fetchCompanies();
      } catch (err: any) {
        showToast(err.message, { type: 'error' });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleDelete = async (company: ICompany) => {
    if (!window.confirm(`Are you sure you want to delete ${company.name}?`)) return;
    try {
      await companyService.deleteCompany(company._id);
      showToast('Company deleted.', { type: 'success' });
      fetchCompanies();
    } catch (err: any) {
      showToast(err.message, { type: 'error' });
    }
  };
  
  const openCreateModal = () => {
    setEditingCompany(null);
    formik.resetForm();
    setIsModalOpen(true);
  };
  
  const openEditModal = (company: ICompany) => {
    setEditingCompany(company);
    setIsModalOpen(true);
  };

  if (isLoading) return <Loader />;

  return (
    <div className="py-10 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Manage Companies</h1>
          <button onClick={openCreateModal} className="bg-blue-600 text-white px-4 py-2 rounded-md ...">
            <Plus className="w-4 h-4 mr-2" /> Add Company
          </button>
        </div>
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <ul className="divide-y divide-gray-200">
            {companies.map(company => (
              <li key={company._id} className="px-6 py-4 flex items-center justify-between">
                <div className="flex items-center">
                  <img src={company.logo} alt={company.name} className="h-10 w-24 object-contain mr-4"/>
                  <span className="font-medium">{company.name}</span>
                </div>
                <div className="flex items-center space-x-4">
                    <span className={`px-2 text-xs font-semibold rounded-full ${company.featured ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {company.featured ? 'Featured' : 'Standard'}
                    </span>
                    <button onClick={() => openEditModal(company)} className="text-indigo-600 hover:text-indigo-900"><Edit size={18}/></button>
                    <button onClick={() => handleDelete(company)} className="text-red-600 hover:text-red-900"><Trash2 size={18}/></button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingCompany ? 'Edit Company' : 'Add New Company'}>
        <form onSubmit={formik.handleSubmit} className="space-y-4">
            <FormField
                label="Company Name"
                name="name"
                type="text"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && formik.errors.name ? formik.errors.name : undefined}
            />
            <FormField
                label="Logo URL"
                name="logo"
                type="text"
                value={formik.values.logo}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.logo && formik.errors.logo ? formik.errors.logo : undefined}
            />
            <div className="flex items-center">
                {/* ✅ FIX: Replaced Formik's <Field> with a standard <input> */}
                <input
                    type="checkbox"
                    name="featured"
                    id="featured"
                    checked={formik.values.featured}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="h-4 w-4 rounded"
                />
                <label htmlFor="featured" className="ml-2 text-sm">Feature this company on the homepage</label>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 bg-gray-200 rounded-md">Cancel</button>
                <button type="submit" disabled={formik.isSubmitting} className="px-4 py-2 bg-blue-600 text-white rounded-md">
                    {formik.isSubmitting ? <Loader /> : (editingCompany ? 'Update Company' : 'Add Company')}
                </button>
            </div>
        </form>
      </Modal>
    </div>
  );
};

export default ManageCompanies;